import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  fetchAttendanceMetadata,
  fetchCurrentUserMonthlyReport,
} from "../../store/slices/attendanceSlice";
import { ChevronDown, CheckCircle, Clock, MapPin } from "lucide-react";
import {
  formatLocation,
  formatTime,
  getStatusIcon,
} from "../../utils/attendanceUtils";
import AttendanceRegularizeModal from "./RegularizeModal";
import { RegularizationPayload } from "../../models/regularize.model";
import { submitRegularization } from "../../store/slices/regularizeSlice";
import toast from "react-hot-toast";
import { fetchEmployeeDetails } from "../../store/slices/employeeSlice";
import { isWeekend as checkWeekend } from "../../utils/attendanceUtils";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const CurrentUserAttendancePage: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const today = new Date();
  const currentDate = today.toISOString().split("T")[0];
  
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  
  const { currentUserMonthlyRecords, loading } = useSelector(
    (state: RootState) => state.attendance
  );
  const userId = useSelector((state: RootState) => state.auth.userId);
  const holidays = useSelector((state: RootState) => state.attendance.holidays);
  const employee = useSelector(
    (state: RootState) => state.employee.userDetails
  );
  const weekendPolicy = employee?.weekendPolicy || "general_sun_off_only";

  useEffect(() => {
    dispatch(
      fetchCurrentUserMonthlyReport({
        month: selectedMonth,
        year: selectedYear,
      })
    );
  }, [selectedMonth, selectedYear, dispatch]);
  
  useEffect(() => {
    dispatch(fetchAttendanceMetadata());
  }, [dispatch]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchEmployeeDetails(userId));
    }
  }, [dispatch]);

  const holidayMap = useMemo(() => {
    const map: Record<string, string> = {};
    (holidays || []).forEach((h) => {
      const key = new Date(h.date).toISOString().slice(0, 10);
      map[key] = h.name;
    });
    return map;
  }, [holidays]);

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const onDateSelect = (date: string) => {
    setSelectedDate(date);
    setModalOpen(true);
  };

  const handleDayClick = (dateKey: string) => {
    // Optional: you can check if attendance exists or allow for any date
    setSelectedDate(dateKey);
    setModalOpen(true);
  };

  const handleRegularizeSubmit = (data: RegularizationPayload) => {
    dispatch(submitRegularization(data))
      .unwrap()
      .then(() => {
        setModalOpen(false);
      })
      .catch((error) => {
        toast.error(error);
      });
  };

  // Build a date-keyed map of records for quick lookup
  const monthlyMap = useMemo(() => {
    const map: Record<string, (typeof currentUserMonthlyRecords)[0]> = {};
    currentUserMonthlyRecords.forEach((rec) => {
      const d = new Date(rec.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
      map[key] = rec;
    });
    return map;
  }, [currentUserMonthlyRecords]);

  // Prepare weeks for calendar view
  const firstDay = new Date(selectedYear, selectedMonth - 1, 1);
  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
  const weeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = Array(firstDay.getDay()).fill(null);

  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  while (currentWeek.length < 7) currentWeek.push(null);
  weeks.push(currentWeek);

  const generateYearOptions = () => {
    const years: number[] = [];
    const currentYear = today.getFullYear();
    for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);
    return years;
  };

  const totalPresentCount = currentUserMonthlyRecords.filter(
    (rec) =>
      rec.firstSessionStatus?.toLowerCase() === "present" ||
      rec.secondSessionStatus?.toLowerCase() === "present"
  ).length;

  const statusColors: Record<string, { bg: string; text: string }> = {
    present: { bg: "bg-green-100", text: "text-green-700" },
    absent: { bg: "bg-red-100", text: "text-red-700" },
    "paid leave": { bg: "bg-yellow-100", text: "text-yellow-700" },
    "sick leave": { bg: "bg-blue-100", text: "text-blue-700" },
    "casual leave": { bg: "bg-purple-100", text: "text-purple-700" },
    compoff: { bg: "bg-pink-100", text: "text-pink-700" },
    "loss of pay": { bg: "bg-gray-100", text: "text-gray-700" },
    holiday: { bg: "bg-orange-100", text: "text-orange-700" },
    weekend: { bg: "bg-sky-100", text: "text-sky-700" },
  };

  return (
    <div className="max-w-6xl mx-auto p-1">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">
              Monthly Attendance
            </h3>
            <p className="text-xs text-gray-600">
              View detailed attendance for the selected period
            </p>
          </div>
          {/* Date Selectors */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-xs font-medium text-gray-700"
              >
                {monthNames.map((name, idx) => (
                  <option key={idx} value={idx + 1}>
                    {name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            <div className="relative">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-xs font-medium text-gray-700"
              >
                {generateYearOptions().map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            <div className="flex items-center text-gray-700 text-xs font-medium px-3 py-2">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span>Total Present: {totalPresentCount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="p-4 border border-gray-200 border-t-0 rounded-b-xl">
        {loading ? (
          <div className="text-center text-gray-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-full">
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-px mb-px bg-gray-200">
                {dayNames.map((dayName, index) => (
                  <div
                    key={dayName}
                    className={`p-3 text-center text-xs font-semibold bg-gray-50 ${
                      index === 0 || index === 6
                        ? "text-blue-600"
                        : "text-gray-700"
                    }`}
                  >
                    {dayName}
                  </div>
                ))}
              </div>

              {/* Days */}
              <div className="grid grid-cols-7 gap-px bg-gray-200">
                {weeks.map((week, wi) =>
                  week.map((day, di) => {
                    if (day === null) {
                      return (
                        <div key={`${wi}-${di}`} className="h-32 bg-gray-50" />
                      );
                    }

                    const dateKey = `${selectedYear}-${String(
                      selectedMonth
                    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                    const rec = monthlyMap[dateKey];
                    const isToday = dateKey === currentDate;
                    const dateObj = new Date(
                      selectedYear,
                      selectedMonth - 1,
                      day
                    );
                    const isWeekend = checkWeekend(dateObj, weekendPolicy);
                    const isHoliday = !!holidayMap[dateKey];

                    return (
                      <div
                        key={`${wi}-${di}`}
                        onClick={() => handleDayClick(dateKey)}
                        className={`h-26 bg-white p-2 relative overflow-hidden hover:bg-gray-50 transition-colors cursor-pointer ${
                          isToday
                            ? "ring-2 ring-navyBlue-primary ring-inset"
                            : ""
                        }`}
                      >
                        <div
                          className={`text-xs font-semibold mb-1 ${
                            isToday
                              ? "text-blue-600"
                              : isWeekend
                              ? "text-gray-500"
                              : "text-gray-900"
                          }`}
                        >
                          {day}
                        </div>
                        {rec && rec.punchIn ? (
                          <div className="space-y-1">
                            <div className="space-y-1 text-xs font-medium">
                              {rec.firstSessionStatus && (
                                <div
                                  className={`inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-medium ${
                                    statusColors[
                                      rec.firstSessionStatus.toLowerCase()
                                    ]?.bg
                                  } ${
                                    statusColors[
                                      rec.firstSessionStatus.toLowerCase()
                                    ]?.text
                                  }`}
                                >
                                  {" "}
                                  {getStatusIcon(rec.firstSessionStatus)}
                                  <span className="capitalize">
                                    1st: {rec.firstSessionStatus}
                                  </span>
                                </div>
                              )}
                              {rec.secondSessionStatus && (
                                <div
                                  className={`inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-medium ${
                                    statusColors[
                                      rec.secondSessionStatus.toLowerCase()
                                    ]?.bg
                                  } ${
                                    statusColors[
                                      rec.secondSessionStatus.toLowerCase()
                                    ]?.text
                                  }`}
                                >
                                  {" "}
                                  {getStatusIcon(rec.secondSessionStatus)}
                                  <span className="capitalize">
                                    2nd: {rec.secondSessionStatus}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="text-xs">
                              <div className="flex items-center gap-1 text-green-teal">
                                <Clock className="w-3 h-3" />
                                <span>In:</span>
                                <span>{formatTime(rec.punchIn)}</span>
                              </div>
                              <div className="flex items-center gap-1 text-red-secondary">
                                <Clock className="w-3 h-3" />
                                <span>Out:</span>
                                <span>
                                  {rec.punchOut ? formatTime(rec.punchOut) : ""}
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-16">
                            {isHoliday ? (
                              <div
                                className={`inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-medium ${statusColors["holiday"].bg} ${statusColors["holiday"].text}`}
                              >
                                {holidayMap[dateKey]}
                              </div>
                            ) : isWeekend ? (
                              <div
                                className={`inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-medium ${statusColors["weekend"].bg} ${statusColors["weekend"].text}`}
                              >
                                Weekend
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 font-medium">
                                No record
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                {/* Modal for attendance regularization */}
                <AttendanceRegularizeModal
                  isOpen={modalOpen}
                  onClose={() => setModalOpen(false)}
                  date={selectedDate || ""}
                  onSubmit={handleRegularizeSubmit}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentUserAttendancePage;
